import { useCallback, useEffect, useState } from 'react';
import useRequest from 'hooks/useRequest';

// Fase 5 — hook para leer el semáforo de cumplimiento MINVU de un cliente
// o edificio. Pega a GET /clientes/:id/compliance o /edificios/:id/compliance
// (compute on-demand en el backend). El resultado se re-lee al montar y
// cada vez que el caller invoque refresh().

export function useClienteCompliance(idCliente) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useRequest();

  const refresh = useCallback(async () => {
    if (!idCliente) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await request(`/clientes/${idCliente}/compliance`, 'GET');
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [idCliente, request]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useEdificioCompliance(idEdificio) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useRequest();

  const refresh = useCallback(async () => {
    if (!idEdificio) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await request(`/edificios/${idEdificio}/compliance`, 'GET');
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [idEdificio, request]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

/**
 * Variante batch: una sola llamada alimenta la columna de compliance del
 * listado superior de clientes. Devuelve un Map id_cliente → summary.
 */
export function useClientesComplianceBatch() {
  const [dataByCliente, setDataByCliente] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useRequest();

  const fetchBatch = useCallback(
    async (clienteIds) => {
      if (!Array.isArray(clienteIds) || clienteIds.length === 0) return {};
      setLoading(true);
      setError(null);
      try {
        const arr = await request('/compliance/batch', 'POST', { clienteIds });
        const map = {};
        for (const row of Array.isArray(arr) ? arr : []) {
          map[row.id_cliente] = row;
        }
        setDataByCliente(map);
        return map;
      } catch (err) {
        setError(err);
        return {};
      } finally {
        setLoading(false);
      }
    },
    [request]
  );

  return { dataByCliente, loading, error, fetchBatch };
}
